/**
 * Merges old data with new data without duplicates based on a unique identifier.
 * Supports nested identifiers like 'event.id'
 *
 * @param {Object} params - Parameters for merging data.
 * @param {Array} params.oldData - The existing data.
 * @param {Array} params.newData - The new data to be merged.
 * @param {string} params.identifier - The unique identifier key to check for duplicates (e.g., 'id' or 'event.id').
 * @returns {Array} - The merged data without duplicates.
 */

export const mergeDataWithoutDuplicates = ({ oldData = [], newData = [], identifier = "id" }) => {
  try {
    // Function to safely get value from nested path like 'event.id'
    const getValueByPath = (obj, path) => {
      const parts = path.split(".")
      return parts.reduce((value, key) => (value && value[key] !== undefined ? value[key] : undefined), obj)
    }

    // Create a set of identifiers from the old data
    const existingItemIdentifiers = new Set(
      oldData?.map(item => getValueByPath(item, identifier)).filter(id => id !== undefined)
    )

    // Filter out new items that already exist in old data
    const uniqueNewItems =
      newData?.filter(newItem => {
        const newItemId = getValueByPath(newItem, identifier)
        return newItemId !== undefined && !existingItemIdentifiers.has(newItemId)
      }) ?? []

    return [...uniqueNewItems, ...(oldData ?? [])]
  } catch (e) {
    console.error("Error merging data:", e)
    return []
  }
}
